// Activity-type vocabulary shared by AddEditActivity (the create/edit modal)
// and the Activities list. Nikhil 2026-09-12: Activities are the record of
// CONTACT only (call / meeting / email / onboarding). Anything a rep must DO
// is a Task (cs-analysis actionables), so 'task' and 'deadline' are retired
// from every picker + filter legend. Existing rows of those types keep
// rendering with their icons (history must not disappear) — hence the split
// between the pickable list and the full icon map.

export const ACTIVITY_TYPES = ['call', 'meeting', 'email', 'onboarding'];

// Retired types: never offered for new activities, still rendered when read.
export const LEGACY_ACTIVITY_TYPES = ['task', 'deadline'];

export const ACTIVITY_TYPE_ICONS = {
    call: 'phone',
    meeting: 'users',
    email: 'envelope',
    onboarding: 'handshake',
    task: 'clock',
    deadline: 'flag',
};

export const is_legacy_activity_type = (type) => LEGACY_ACTIVITY_TYPES.includes(type);

// Types offered by the create/edit picker. A legacy row being EDITED keeps its
// own type selectable (so the modal reflects what the row is) — but a rep can
// never pick a legacy type for a new activity.
export const pickable_activity_types = (current_type, mode) => {
    if (mode === 'edit' && is_legacy_activity_type(current_type)) return [...ACTIVITY_TYPES, current_type];
    return ACTIVITY_TYPES;
};

// Activities-list visibility for a row's type under the legend's selection
// map. Legacy types have no legend toggle any more, so they're always visible
// — a saved filter with {task: 0} from before the change must not hide history
// with no way to bring it back.
export const activity_type_visible = (selected_types, type) => {
    if (is_legacy_activity_type(type)) return true;
    return !!(selected_types && selected_types[type]);
};

// The id of the activity cs_backend just created. general_fetch unwraps the
// `data` envelope, so the create response is the saved Activity row itself
// (`activity_id`) — the other shapes are tolerated for older/newer backends.
export const created_activity_id = (resp) => {
    if (!resp || typeof resp !== 'object') return null;
    const id = resp.activity_id
        || resp.id
        || (resp.activity && (resp.activity.activity_id || resp.activity.id))
        || (resp.data && (resp.data.activity_id || resp.data.id));
    return id ? String(id) : null;
};

// Whether the note holds the block on lines of its own: starting at a line's
// start and ending at a line's end, whatever a rep wrote around it. The same
// words inside a sentence of the note are not the block.
const holds_block = (note, block) => {
    for (let at = note.indexOf(block); at !== -1; at = note.indexOf(block, at + 1)) {
        const end = at + block.length;
        const starts_line = at === 0 || note[at - 1] === '\n';
        const ends_line = end === note.length || note[end] === '\n' || note[end] === '\r';
        if (starts_line && ends_line) return true;
    }
    return false;
};

// The activity's note with a draft appended (a meeting's notes drafted by
// cs-analysis, saved only when the rep presses Update): the existing note, a
// blank line, the draft. A note that already holds the draft on lines of its
// own is left as it is, so opening the dialog twice never adds it twice.
export const append_note = (existing, draft) => {
    const base = typeof existing === 'string' ? existing.trimEnd() : '';
    const add = typeof draft === 'string' ? draft.trim() : '';
    if (!add) return base;
    if (!base) return add;
    if (holds_block(base, add)) return base;
    return `${base}\n\n${add}`;
};
